import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetDeliveryNoteMutation } from '../../../../store/api/deliveryNoteApi';
import ActionButtonDropdown from "../../../../Components/ActionButtonDropdown";
import Pagination from "../../../../Core/Pagination/Pagination";
import ListLayout from "../../../../Core/Layout/ListLayout";
import Table from "../../../../Core/Table/Table";
import Tabs from "../../../../Components/Tabs";
import ConfigureColumnsNew from "../../../../Core/ConfigureColumnsNew/ConfigureColumnsNew";
import ExportCSV from '../../../../Core/Export/ExportCSV';
import ListFilters from "../../../../Core/ListFilters/ListFilters";
import DelConfirmPopup from "../../../../Components/Popup/DelConfirmPopup ";
import { useDeleteDocMutation } from "../../../../store/api/globalapi";
import useScreenHeight from "../../../../utils/useScreenHeight";

const Lpntable = () => {
  const navigate = useNavigate();
  const { screenHeight, screenScaling , pageSize} = useScreenHeight();
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [deliveryNoteData, setDeliveryNoteData] = useState({ delivery_note_data: [], total_no_of_records: 0 });
  const [deliveryNoteDataApi] = useGetDeliveryNoteMutation();
  const [showExportPopup, setShowExportPopup] = useState(false);
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [deleteDoc] = useDeleteDocMutation();
  const deliveryNoteDataRef = useRef();
  useEffect(() => {
    deliveryNoteDataRef.current = deliveryNoteData;
  }, [deliveryNoteData]);
  const filtersConfig = [
    { name: 'siNo', label: 'S. No.', width: '6%', type: 'number' },
    { name: 'name', label: 'Delivery Note Id', width: '15%', type: 'text' },
    {
      name: 'status', label: 'Status', width: '10%', type: 'select', options: [
        { value: 'Draft', label: 'Draft' },
        { value: 'To Bill', label: 'To Bill' },
        { value: 'Completed', label: 'Completed' },
        { value: 'Cancelled', label: 'Cancelled' },
        { value: 'Return Issued', label: 'Return Issued' },
      ]
    },
    { name: 'creation', label: 'Creation Date', type: 'date', width: '12%' },
    { name: 'company', label: 'Company', type: 'text', width: '12%' },
    { name: 'customer', label: 'Customer', type: 'text', width: '15%' },
    { name: 'posting_date', label: 'Posting Date', type: 'date', width: '12%' },
    { name: 'pick_list', label: 'Pick List', type: 'text', width: '15%' },
    { name: 'taxable_amount', label: 'Taxable Amount', type: 'number', width: '12%' },
    { name: 'gst_amount', label: 'GST Amount', type: 'number', width: '12%' },
    { name: 'total_value', label: 'Total Value', type: 'number', width: '12%' },
    { name: 'customer_address', label: 'Customer Address', type: 'text', width: '15%' },
    { name: 'shipping_address', label: 'Shipping Address', type: 'text', width: '15%' },
    { name: 'created_by', label: 'Created By', type: 'text', width: '15%' },
    { name: 'billing_address', label: 'Billing Address', type: 'text', width: '15%' },
    { name: 'gstin', label: 'GSTIN', type: 'text', width: '12%' },
    { name: 'no_of_line', label: 'No. of Lines', type: 'number', width: '10%' },
    { name: 'total_qty', label: 'Total Quantity', type: 'number', width: '10%' },
    { name: 'order_number', label: 'Order Number', type: 'text', width: '15%' },
    { name: 'box_id', label: 'Box ID', type: 'text', width: '10%' },
    { name: 'date_range', label: 'Date Range', type: 'date-range', width: '12%' },
  ];

  const [columnsConfig, setColumnsConfig] = useState(() => [
    {
      header: "S. No.",
      name: "S. No.",
      accessor: "siNo",
      width: '72px', // ~6%
      cellRenderer: (row) => row.siNo,
      sortable: false,
      visible: true
    },
    {
      header: "Delivery Note Id",
      name: "Name",
      accessor: "name",
      width: '180px', // ~15%
      sortable: true,
      visible: true
    },
    {
      header: "Sales Order No",
      name: "Sales Order No",
      accessor: "order_number",
      width: '180px',
      sortable: true,
      visible: false
    },
    {
      header: "Pick List Id",
      name: "Pick List Id",
      accessor: "pick_list",
      width: '180px',
      sortable: true,
      visible: false
    },
    {
      header: "Customer",
      name: "Customer",
      accessor: "customer",
      width: '180px',
      sortable: true,
      visible: true
    },
    {
      header: "No. of Lines",
      name: "No. of Lines",
      accessor: "no_of_line",
      width: '144px', // ~12%
      sortable: true,
      visible: true,
      sortFn: (a, b) => parseInt(a.no_of_line) - parseInt(b.no_of_line)
    },
    {
      header: "Total Quantity",
      name: "Total Quantity",
      accessor: "total_qty",
      width: '144px',
      sortable: true,
      visible: true,
      sortFn: (a, b) => parseFloat(a.total_qty) - parseFloat(b.total_qty)
    },
    {
      header: "Taxable Amount",
      name: "Taxable Amount",
      accessor: "taxable_amount",
      width: '144px',
      sortable: true,
      visible: true,
      sortFn: (a, b) => parseFloat(a.taxable_amount) - parseFloat(b.taxable_amount)
    },
    {
      header: "GST Amount",
      name: "GST Amount",
      accessor: "gst_amount",
      width: '144px',
      sortable: true,
      visible: false,
      sortFn: (a, b) => parseFloat(a.gst_amount) - parseFloat(b.gst_amount)
    },
    {
      header: "Total Value",
      name: "Total Value",
      accessor: "total_value",
      width: '144px',
      sortable: true,
      visible: false,
      sortFn: (a, b) => parseFloat(a.total_value) - parseFloat(b.total_value)
    },
    {
      header: "Billing Address",
      name: "Billing Address",
      accessor: "billing_address",
      width: '216px',
      sortable: false,
      visible: false
    },
    {
      header: "Shipping Address",
      name: "Shipping Address",
      accessor: "shipping_address",
      width: '216px',
      sortable: false,
      visible: false
    },
    {
      header: "Status",
      name: "Status",
      accessor: "status",
      width: '120px',
      sortable: true,
      visible: true,
      cellRenderer: (row) => {
        const status =
          row.docstatus === 1 && row.is_return === 1
            ? "Return"
            : row.status;

        let color;
        switch (status) {
          case 'Partially Picked': color = '#F97316'; break;
          case 'Draft': color = '#6366F1'; break;
          case 'Completed': color = '#65A30D'; break;
          case 'Open': color = '#6B21A8'; break;
          case 'Pick List Created': color = '#0E7490'; break;
          case 'To Bill': color = '#D97706'; break;
          case 'Completely Picked': color = '#65A30D'; break;
          case 'Cancelled': color = '#EF4444'; break;
          case 'Closed': color = '#E11D48'; break;
          case 'Return': color = '#0891B2'; break;
          default: color = '#000000';
        }

        return (
          <span
            style={{
              backgroundColor: `${color}1A`,
              color,
              padding: '4px 10px',
              borderRadius: '9999px',
              fontSize: '12px',
              fontWeight: 500,
              display: 'inline-block',
              textTransform: 'capitalize',
            }}
          >
            {status}
          </span>
        );
      }
    },

    {
      header: "Date",
      name: "Date",
      accessor: "creation",
      width: '180px',
      sortable: true,
      visible: true,
      sortFn: (a, b) => new Date(a.creation) - new Date(b.creation)
    },
    {
      header: "Action",
      name: "Action",
      accessor: "action",
      width: '120px',
      sortable: false,
      visible: true,
      cellRenderer: (row) => (
        <ActionButtonDropdown
          id={row.name}
          onView={() => handleView(row.name)}
          onEdit={() => handleEdit(row.name)}
          onDelete={() => handleDelete(row.name)}
          actions={['view', 'delete', 'edit']}
        />
      )
    }
  ]);



  const handleSaveColumns = (selectedKeys) => {
    const updatedColumns = columnsConfig.map(column => ({
      ...column,
      visible: selectedKeys.includes(column.accessor)
    }));
    setColumnsConfig(updatedColumns);
  };

  const visibleColumns = useMemo(() =>
    columnsConfig.filter(col => col.visible), [columnsConfig]);

  const defaultSelectedColumns = useMemo(() =>
    columnsConfig
      .filter(col => col.visible)
      .map(col => ({
        fieldname: col.accessor,
        label: col.name
      })), [columnsConfig]);

  // const handleView = (id) => navigate(`/outbound/delivery-note-view`, { state: { delivery_note_id: id } });
  const handleView = (id) => {

    const deliveryNote = deliveryNoteDataRef.current.delivery_note_data.find(item => item.name === id);

    if (deliveryNote.is_return === 1) {
      console.log(deliveryNoteData, "purchaseData.delivery_note_data");

      navigate("/outbound/view-sales-return/", { state: { delivery_note_id: id } });
    } else {
      console.log("not_foud");

      navigate(`/outbound/delivery-note-view`, { state: { delivery_note_id: id } });
    }

  };
  const handleEdit = (id) => {
    const deliveryNote = deliveryNoteDataRef.current.delivery_note_data.find(
			(item) => item.name === id
		);
    if (deliveryNote.is_return === 1) {
      navigate("/outbound/edit-sales-return/", { state: { delivery_note_id: id } });
    } else {
       navigate(`/outbound/delivery-note-edit`, { state: { delivery_note_id: id } });
      
      // alert("You can only edit available Sales Returns");
    }

  };


  const [delPopupVisible, setDelPopupVisible] = useState(false);
  const [delSelectedId, setDelSelectedId] = useState(null);
  const [delmessage, setDelmessage] = useState();
  const [alreadySubmitData, setAlreadySubmitData] = useState(false)
  const [delDone, setDelDone] = useState(false)

  const handleDelete = (id) => {
    setDelSelectedId(id);
    setDelPopupVisible(true)
    setAlreadySubmitData(false)
    setDelmessage("Are you sure you want to Delete this Delivery Note?");
  };

  const delHandleCancel = () => {
    setDelPopupVisible(false);
    setDelSelectedId(null);
  }

  const delHandleConfirm = async () => {

    try {
      await deleteDoc({ doctype: "Delivery Note", name: delSelectedId }).unwrap()
      setDelmessage(`Delivery Note ${delSelectedId} deleted successfully!`)
      setDelDone(true)
      setTimeout(() => {
        setDelPopupVisible(false);
        setDelDone(false)
        getData();
      }, 1000);
    } catch (err) {
      console.error("Deletion Failed:", err)

      let errorMessage = "Failed to delete the Delivery Note!";

      //If the server is sending the error message , we will extract it.
      if (err?.data?._server_messages) {
        try {
          const messageArr = JSON.parse(err.data._server_messages)
          if (messageArr && messageArr.length > 0) {
            //parse inner json object
            const messageObj = JSON.parse(messageArr[0])
            if (messageObj?.message) {
              //extract the exact message we want
              const fullMessage = messageObj.message
              errorMessage = fullMessage.trim();
              //remove amu html in the message.
              errorMessage = errorMessage.replace(/<[^>]+>/g, '');
            }
          }
        } catch (parseError) {
          console.error("Error parsing server messages:", parseError);
        }
      }
      setDelPopupVisible(true)
      setDelmessage(errorMessage)
      setAlreadySubmitData(true);

    } finally {
      // setTimeout(() => {
      //   setDelPopupVisible(false);
      //   setDelSelectedId(null);
      // }, 2000);
    }
  };

  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter(val => val !== null && val !== undefined && val !== '').length;
  }, [filters]);
  const actionButtons = [
    {
      className: "createbtn border-0 d-flex",
      style: { gap: '2px', height: '28px' },
      iconName: "IoFilterSharp",
      iconSize: 12,
      children: activeFilterCount > 0 ? `Filter (${activeFilterCount})` : "Filter",
      onClick: () => {
        setShowFilters(prev => !prev);
        setShowColumnConfig(false);
      }
    },
    {
      className: "createbtn border-0 d-flex",
      style: { gap: '2px', height: '28px' },
      iconName: "MdOutlineRemoveRedEye",
      iconSize: 12,
      children: "Show more",
      onClick: () => {
        setShowColumnConfig(prev => !prev);
        setShowFilters(false);
      }
    },
    {
      className: "createbtn border-0 d-flex",
      style: { gap: '2px', height: '28px' },
      iconName: "RiFileExcel2Fill",
      iconSize: 12,
      children: "Export Excel",
      onClick: () => setShowExportPopup(true),
    },
      {
      className: "custom-btn-00989 d-flex",
      style: { gap: '2px', height: '28px' },
      iconName: "FiPlus",
      iconSize: 12,
      children: "Create Delivery Note",
      to: "/outbound/outbound-create-delivery-note",
    }
  ];

  const getData = async () => {
    setLoading(true);
    try {
			const filteredSearchInput = Object.entries(filters).reduce(
				(acc, [key, value]) => {
					if (value !== "" && value !== undefined && value !== null) {
						acc[key] = value;
					}
					return acc;
				},
				{}
			);
			const siNoFilter = filteredSearchInput.siNo;
			const isSiNoFilterActive = siNoFilter !== undefined && siNoFilter !== "";
			// STEP 3: If filtering by siNo, jump to that page
			const targetPage = isSiNoFilterActive
				? Math.ceil(parseInt(siNoFilter, 10) / pageSize)
				: currentPage;
			const payload = {
				start: pageSize * (targetPage - 1),
				page_length: pageSize,
				...(Object.keys(filteredSearchInput).length > 0 && {
					search_input: {
						delivery_note_no: filteredSearchInput.delivery_note_no || "",
						customer: filteredSearchInput.customer || "",
						pick_list: filteredSearchInput.pick_list || "",
						order_number: filteredSearchInput.order_number || "",
						customer_address: filteredSearchInput.customer_address || "",
						shipping_address: filteredSearchInput.shipping_address || "",
						billing_address: filteredSearchInput.billing_address || "",
						gstin: filteredSearchInput.gstin || "",
						created_by: filteredSearchInput.created_by || "",
						company: filteredSearchInput.company || "",
						no_of_line: filteredSearchInput.no_of_line || "",
						total_qty: filteredSearchInput.total_qty || "",
						taxable_amount: filteredSearchInput.taxable_amount || "",
						gst_amount: filteredSearchInput.gst_amount || "",
						total_value: filteredSearchInput.total_value || "",
						status: filteredSearchInput.status || "",
						date_range: filteredSearchInput.date_range || "",
					},
				}),
			};
      // console.log("Page size fetch",pageSize)
			const response = await deliveryNoteDataApi(payload);
			const rawData = response?.data?.data?.delivery_note_data;
			// STEP 4: Assign global serial numbers
			const numberedData = rawData.map((item, index) => ({
				...item,
				siNo: index + 1 + pageSize * (targetPage - 1),
			}));

			// STEP 5: If siNo is used, filter client-side
			const finalData = isSiNoFilterActive
				? numberedData.filter((item) => item.siNo === parseInt(siNoFilter, 10))
				: numberedData;
      setDeliveryNoteData({
        delivery_note_data: finalData || [],
        total_no_of_records: response?.data?.data?.total_no_of_records|| 0,
      });
				if (isSiNoFilterActive) setCurrentPage(targetPage);

		} catch (error) {
      console.error("Error fetching delivery notes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setCurrentPage(1);
    setFilters(newFilters);
  };

  useEffect(() => {
    if (pageSize > 0)
    {
    }  getData();
      
	}, [currentPage, filters, pageSize, screenHeight, screenScaling]);
  const accessors = columnsConfig
		.filter(
			(column) => column.accessor !== "siNo" && column.accessor !== "action"
		)
		.map((column) => column.accessor);
  return (
    <div className="container-fluid d-flex flex-column" style={{ flexGrow: '1' }}>
      <Tabs />
      <ListLayout
        breadcrumbItems={[
          { label: "Outbound" },
          { label: "Delivery Note List", style: { color: '#270B79' } }
        ]}
        actionButtons={actionButtons}
      >
        {showFilters && (
          <ListFilters
            filtersConfig={filtersConfig}
            onFilterChange={handleFilterChange}
            currentFilters={filters}
            setShowFilters={setShowFilters}
          />
        )}

        {showColumnConfig && (
          <ConfigureColumnsNew
            show={showColumnConfig}
            onClose={() => setShowColumnConfig(false)}
            onSave={handleSaveColumns}
            docType="Delivery Note"
            defaultSelected={defaultSelectedColumns}
            fieldsData={columnsConfig.map(col => ({
              fieldname: col.accessor,
              label: col.name
            }))}
            disabledFields={["siNo", "name", "status", "creation", "action"]}
          />
        )}

        <Table
          columns={visibleColumns}
          data={deliveryNoteData.delivery_note_data}
          loading={loading}
        />

        <Pagination
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          totalRecords={deliveryNoteData.total_no_of_records}
          currentPage={currentPage}
          className="mt-2 pb-3"
        />
        {delPopupVisible &&
          <DelConfirmPopup
            alredysubmitdata={alreadySubmitData}
            delSelectedId={delSelectedId}
            visible={delPopupVisible}
            message={delmessage}
            onConfirm={delHandleConfirm}
            onCancel={delHandleCancel}
            delDone={delDone}
          />}
        {showExportPopup && (
          <ExportCSV
            doctype="Delivery Note"
            fieldsData={accessors}
            onClose={() => setShowExportPopup(false)}
          />
        )}
      </ListLayout>
    </div>
  );
};

export default Lpntable;